/* eslint-disable unicorn/consistent-function-scoping */
import { ref, nextTick } from "vue";
import { defineStore } from "pinia";

import { getNumber } from "@/utils";
import { getDocumentByUrl, reattachScripts, getEntriesElem, getPaginationElem } from "@/utils/dom";

import useStateStore from "@stores/state";

export default defineStore("entries", () => {
  // constants
  const pageHTML = ref<string>('');
  const entriesHTML = ref<string>('');
  const entriesScripts = ref<HTMLScriptElement[]>([]);
  const paginationTotal = ref<number>(1);
  const paginationCurrent = ref<number>(1);

  // stores
  const stateStore = useStateStore();

  // methods
  const prepareFilterUrl = (filters: Record<string, unknown>) => {
    // create object copy to avoid mutation
    const filtersCopy = { ...filters };
    const filtersCopyHasItems = Object.keys(filtersCopy).length > 0;

    // prepare new url
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL(currentUrl.origin + currentUrl.pathname + currentUrl.search);

    // convert filters to url encoded JSON string
    const urlEncodedFilters = filtersCopyHasItems
      ? encodeURIComponent(JSON.stringify(filtersCopy))
      : undefined;

    // set or delete filters
    if (urlEncodedFilters) {
      newUrl.searchParams.set('filters', urlEncodedFilters);
    } else {
      newUrl.searchParams.delete('filters');
    }

    // return new url
    return newUrl;
  }
  const preparePaginationUrl = (page: number) => {
    // prepare new url
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL(currentUrl.origin + currentUrl.pathname + currentUrl.search);
    newUrl.searchParams.set('page', page.toString());

    // return new url
    return newUrl;
  }

  const updateEntries = async (id: string, newDoc: Document) => {
    // get entries element
    const newEntriesElem = getEntriesElem(newDoc, id);

    // clear old scripts
    if (entriesScripts.value.length > 0) {
      for (const script of entriesScripts.value) {
        script.remove();
      }
    }

    // if found, update entries
    if (newEntriesElem) {

      // set entries HTML
      const newHTML = newEntriesElem.innerHTML;
      entriesHTML.value = newHTML;

      // wait for next tick
      await nextTick();

      // set entries scripts
      const newScripts = reattachScripts(newEntriesElem);
      entriesScripts.value = newScripts;
    }
  }

  const updatePagination = async (id: string, newDoc: Document) => {
    // get pagination element
    const oldPaginationElem = getPaginationElem(document, id);
    const newPaginationElem = getPaginationElem(newDoc, id);

    // if found, update pagination
    if (oldPaginationElem && newPaginationElem) {

      // get new attributes
      const newCurrentAttr = newPaginationElem.getAttribute('current');
      const newTotalAttr = newPaginationElem.getAttribute('total');

      const newCurrentNumber = getNumber(newCurrentAttr);
      const newTotalNumber = getNumber(newTotalAttr);

      if (newCurrentNumber) {
        paginationCurrent.value = newCurrentNumber;
      }
      if (newTotalNumber) {
        paginationTotal.value = newTotalNumber;
      }
    }
  }

  const applyFilters = async (id: string, filters: Record<string, unknown>) => {
    try {
      // set loading state
      stateStore.isLoading = true;

      // get new document
      const newUrl = prepareFilterUrl(filters);
      const newDoc = await getDocumentByUrl(newUrl.href);

      // set new page HTML
      pageHTML.value = newDoc.documentElement.innerHTML;

      // update entries
      await updateEntries(id, newDoc);

      // update pagination
      await updatePagination(id, newDoc);

      // set loading state
      stateStore.isLoading = false;

      // update URL
      window.history.pushState({}, '', newUrl.href);

    } catch (error) {
      console.error(error);
    }
  }

  const paginationMove = async (id: string, page: number) => {
    try {
      // set loading state
      stateStore.isLoading = true;

      // prepare new url with page
      const newUrl = preparePaginationUrl(page);
      const newDoc = await getDocumentByUrl(newUrl.href);

      // update the entries
      await updateEntries(id, newDoc);

      // update pagination
      await updatePagination(id, newDoc);

      // update loading state
      stateStore.isLoading = false;

      // update the URL
      window.history.pushState({}, '', newUrl.href);

    } catch (error) {
      console.error(error);
    }
  }

  return {
    pageHTML,
    entriesHTML,
    entriesScripts,
    paginationTotal,
    paginationCurrent,
    applyFilters,
    paginationMove,
  }
});
